import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useStoreSelector from 'utils/hooks/useStoreSelector'
import { appConfigStore, themeStore } from 'services/state'
import { getUserRoles, SUPER_USER_ROLE } from 'services/auth/isUserAuthorized'
import { setLocalStoragePreferences } from 'src/App/utils'
import { dateFormatOptions, timeFormatOptions } from './options'
import Section from './components/Section'
import Footer from './components/Footer'
import userActionMenu from './panel-actions'
import { defaultThemes } from 'src/App/constants'
import moment from 'moment'

import './index.scss'

import CHANGELOG from '../../CHANGELOG.md'
import { isEmpty } from 'lodash'

const manualDefaultList = [
  // default documentation TODO
  /*  {
    title: 'Manuale Ublique Plat',
    onClick: () => {},
    icon: 'PDF',
    download: true
  } */
]

const Settings = ({
  themes = defaultThemes,
  librariesVersion,
  urlChangelogComponents,
  productChangelogUrl,
  manualsSettings = []
}) => {
  const manualsFields = [...manualDefaultList, ...manualsSettings]

  const { t } = useTranslation('Common')
  const user = useStoreSelector('user')
  const theme = useStoreSelector('theme')
  const isSuperuser = getUserRoles(user)?.includes(SUPER_USER_ROLE)

  const { formatOptions, wrapTextOptions } = useStoreSelector('appConfig')

  const [formattedDateList, formattedTimeList] = useMemo(
    () => [formatLabel(dateFormatOptions), formatLabel(timeFormatOptions)],
    [t]
  )

  const onThemeChange = useCallback(
    (theme) => {
      themeStore.set(theme)
      setLocalStoragePreferences({ theme: theme })
    },
    [themeStore]
  )

  const themeSelection = useMemo(
    () => ({
      themes,
      currentTheme: theme,
      onThemeChange
    }),
    [themes, onThemeChange, theme]
  )

  const setDateFormat = (date) => {
    const dateFormat = getFormatOptions(date)

    const newFormatOptions = {
      ...formatOptions,
      dateFormat
    }

    appConfigStore.update((state) => ({
      ...state,
      formatOptions: newFormatOptions
    }))

    setLocalStoragePreferences(newFormatOptions)
  }
  const setTimeFormat = (time) => {
    const timeFormat = getFormatOptions(time)

    const newFormatOptions = {
      ...formatOptions,
      timeFormat
    }

    appConfigStore.update((state) => ({
      ...state,
      formatOptions: newFormatOptions
    }))
    setLocalStoragePreferences(newFormatOptions)
  }

  const themesFields = [
    {
      title: t('Theme'),
      onClick: onThemeChange,
      currentTheme: themeSelection.currentTheme,
      themes: themeSelection.themes
    }
  ]

  const dateTimeFields = [
    {
      titleText: t('Date'),
      selectedItem: getInitialItem(formattedDateList, formatOptions.dateFormat),
      items: formattedDateList,
      setFormat: setDateFormat
    },
    {
      titleText: t('Time'),
      selectedItem: getInitialItem(formattedTimeList, formatOptions.timeFormat),
      items: formattedTimeList,
      setFormat: setTimeFormat
    }
  ]

  const setWrapOption = (id, newValue) => {
    const wrapOptions = { ...wrapTextOptions, [id]: newValue }
    appConfigStore.update((state) => ({
      ...state,
      wrapTextOptions: wrapOptions
    }))
    setLocalStoragePreferences(wrapOptions)
  }

  const getWrapTextComponents = () => {
    return [
      {
        id: 'headerWrap',
        label: t('Header'),
        toggled: wrapTextOptions.headerWrap,
        disabled: false
      }
      // ,
      // {
      //   id: 'contentWrap',
      //   label: t('Content'),
      //   toggled: wrapTextOptions.contentWrap,
      //   disabled: false
      // }
    ]
  }

  const wrapTextFields = [
    {
      items: getWrapTextComponents(),
      setWrap: setWrapOption
    }
  ]
  return (
    <div className='settings-main' id='BDD_setting_area_panel'>
      <div className='settings-header'>
        <h5>{t('Settings')}</h5>
      </div>
      <div className='settings-body'>
        <Section t={t} type='radio' fields={themesFields} id='BDD_setting_area_theme'/>
        <Section type='dropdown' title={t('Format')} fields={dateTimeFields}  id='BDD_setting_area_date'/>
        <Section type='toggle' title={t('WrapText')} fields={wrapTextFields}  id='BDD_setting_area_wrap'/>

        {!isEmpty(manualsFields) && (
          <Section
            type='download'
            fields={manualsFields}
            title={t('Manuals')}
          />
        )}
        {isSuperuser && (
          <Section title={t('SuperUser')} fields={userActionMenu(t)} id='BDD_setting_area_superuser'/>
        )}
      </div>
      <Footer
        labels={settingsLabels(t)}
        platform={{
          version: librariesVersion.platform,
          changelog: CHANGELOG
        }}
        components={{
          version: librariesVersion.components,
          changelog: urlChangelogComponents
        }}
        product={{
          version: librariesVersion.product,
          changelog: productChangelogUrl
        }}
        id='BDD_setting_area_footer'
      />
    </div>
  )
}

export default Settings

// -------------------- Utils -------------------- //

const getFormatOptions = ({ label, ...rest }) => rest

const getInitialItem = (list, format) =>
  list.find((item) => item.value === format.value)

const formatLabel = (list) => {
  const exampleDate = moment().set({ hour: 18, minute: 0, second: 0 })
  return list.map((item) => ({
    ...item,
    label: exampleDate.format(item.value)
  }))
}

export const settingsLabels = (t) => ({
  platformLabel: t('Platform'),
  componentsLabel: t('Components'),
  versionsLabel: t('Versions'),
  productLabel: t('PRODUCT')
})
